// /settings — everything that mutates account state. Kept separate from
// /profile so the profile page can be a stable read-mostly self-view (the
// same shape strangers see at /user/:id).

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Avatar } from '../components/Avatar';
import { MediaUpload, type UploadResult } from '../components/MediaUpload';
import type { Community, Me } from '../types/api';

export function Settings() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [me,          setMe]          = useState<Me | null>(null);
    const [communities, setCommunities] = useState<Community[]>([]);
    const [status,      setStatus]      = useState<string>('');

    const load = useCallback(async () => {
        if (!authed) return;
        try {
            const [m, c] = await Promise.all([
                api.get<Me>('/api/me'),
                api.get<{ communities: Community[] }>('/api/communities'),
            ]);
            setMe(m);
            setCommunities(c.communities ?? []);
        } catch (e) { console.error('settings load', e); }
    }, [api, authed]);
    useEffect(() => { load(); }, [load]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see your settings.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (!me) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    return (
        <main>
            <div className="profile-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>Settings</h1>
                    <div className="issue">Your account · <Link to="/profile">back to profile →</Link></div>
                    <div className="rule-bottom" />
                </div>

                <AvatarSection me={me} onChanged={setMe} />

                <ProfileEdit me={me} communities={communities} onSaved={(u) => { setMe(u); setStatus('Saved.'); setTimeout(() => setStatus(''), 2000); }} status={status} />

                <FollowsSection />

                <LocationSection />

                <section className="profile-block danger">
                    <h2>Data &amp; account</h2>
                    <p>You can export or delete your data at any time (POPIA §14).</p>
                    <div className="actions">
                        <a className="plain"
                           href="/api/me/activity/export"
                           download="onhere-activity.json"
                           style={{ padding: '6px 12px', border: '1px solid var(--ink)', textDecoration: 'none', color: 'var(--ink)', fontFamily: '-apple-system, sans-serif', fontSize: 13 }}
                        >
                            Export my activity (JSON)
                        </a>
                        <button type="button" className="plain" disabled>Delete my account (coming)</button>
                    </div>
                </section>

                <AuditLogSection />

                <section className="profile-block">
                    <h2>Legal</h2>
                    <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>
                        The written-down version of what we do and don't do.
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                        <li><Link to="/privacy">Privacy Policy</Link> — what we collect + your POPIA rights</li>
                        <li><Link to="/rules">Community Rules</Link> — what gets published, returned, or removed</li>
                        <li><Link to="/editor-code">Editor Code of Conduct</Link> — how editors are held to account</li>
                    </ul>
                </section>
            </div>
        </main>
    );
}

interface AuditRow {
    action:     string;
    detail:     Record<string, unknown> | null;
    created_at: string;
    staff_name: string;
}

// AuditLogSection — POPIA §14 transparency. Shows the user every time a
// staff member peeked at their affinity/signals via the /admin inspector.
// Empty state is "nobody has looked at you" — reassuring, not scary.
function AuditLogSection() {
    const api = useApi();
    const [log, setLog] = useState<AuditRow[] | null>(null);
    useEffect(() => {
        api.get<{ log: AuditRow[] }>('/api/me/audit-log')
           .then(r => setLog(r.log ?? []))
           .catch(() => setLog([]));
    }, [api]);
    if (log === null) return null;
    return (
        <section className="profile-block">
            <h2>Who's looked at me?</h2>
            <p style={{ fontSize: 12, color: '#666' }}>
                Every time an OnHere staff member opens your profile in the internal admin dashboard, we log it here.
                This is your right under POPIA §14. If something on this list looks wrong, email us.
            </p>
            {log.length === 0 ? (
                <p style={{ fontStyle: 'italic', color: '#888' }}>Nobody has looked. Your profile is your own.</p>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: '-apple-system, sans-serif' }}>
                    <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '1px solid #cfc9b7' }}>
                            <th style={{ padding: '6px 8px' }}>When</th>
                            <th style={{ padding: '6px 8px' }}>Staff member</th>
                            <th style={{ padding: '6px 8px' }}>What</th>
                        </tr>
                    </thead>
                    <tbody>
                        {log.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '6px 8px' }}>{new Date(row.created_at).toLocaleString()}</td>
                                <td style={{ padding: '6px 8px' }}>{row.staff_name}</td>
                                <td style={{ padding: '6px 8px' }}>{row.action}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}

interface EditProps {
    me: Me;
    communities: Community[];
    onSaved: (u: Me) => void;
    status: string;
}

function ProfileEdit({ me, communities, onSaved, status: parentStatus }: EditProps) {
    const api = useApi();
    const [displayName,     setDisplayName]     = useState(me.display_name);
    const [homeCommunityID, setHomeCommunityID] = useState(me.home_community_id ?? '');
    const [status,          setStatus]          = useState('');

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        setStatus('Saving…');
        try {
            const updated = await api.patch<Me>('/api/me', {
                display_name:      displayName,
                home_community_id: homeCommunityID || null,
            });
            onSaved(updated);
            setStatus('');
        } catch (err) {
            setStatus('Save failed: ' + (err as Error).message);
        }
    }

    return (
        <section className="profile-block">
            <h2>Profile</h2>
            <form id="profile-edit" onSubmit={onSubmit}>
                <label>
                    <span>Display name</span>
                    <input type="text" required
                           value={displayName}
                           onChange={(e) => setDisplayName(e.target.value)} />
                </label>
                <label>
                    <span>Home community</span>
                    <select value={homeCommunityID} onChange={(e) => setHomeCommunityID(e.target.value)}>
                        <option value="">— none —</option>
                        {communities.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </label>
                <AnonToggle anonName={me.anon_display_identity} displayName={me.display_name} />
                <LowDataToggle onNote={setStatus} />
                <PushToggle onNote={setStatus} />
                <div className="actions">
                    <button type="submit" className="primary">Save</button>
                    <span id="profile-save-status">{status || parentStatus}</span>
                </div>
            </form>
        </section>
    );
}

function AnonToggle({ anonName, displayName }: { anonName: string; displayName: string }) {
    const [checked, setChecked] = useState(() => localStorage.getItem('onhere_prefer_anon') === '1');
    return (
        <label className="inline-toggle">
            <input type="checkbox" checked={checked} onChange={(e) => {
                setChecked(e.target.checked);
                localStorage.setItem('onhere_prefer_anon', e.target.checked ? '1' : '0');
            }} />
            <span>
                <strong>Post as anonymous by default</strong>
                <em>
                    You appear as {checked ? anonName : displayName} in the corner.
                    Compose modal defaults to anon; flip per-post.
                </em>
            </span>
        </label>
    );
}

function LowDataToggle({ onNote }: { onNote: (msg: string) => void }) {
    const [checked, setChecked] = useState(() => localStorage.getItem('onhere_low_data') === '1');
    return (
        <label className="inline-toggle">
            <input type="checkbox" checked={checked} onChange={(e) => {
                setChecked(e.target.checked);
                localStorage.setItem('onhere_low_data', e.target.checked ? '1' : '0');
                document.body.classList.toggle('low-data', e.target.checked);
                onNote(e.target.checked
                    ? 'Low-data mode on — images hidden until you tap them.'
                    : 'Low-data mode off — images load normally.');
            }} />
            <span>
                <strong>Low-data mode</strong>
                <em>Photos don't download automatically — tap a placeholder to load one. Saves data on capped mobile plans.</em>
            </span>
        </label>
    );
}

function PushToggle({ onNote }: { onNote: (msg: string) => void }) {
    const api = useApi();
    const [checked,   setChecked]   = useState(false);
    const [supported, setSupported] = useState(true);

    useEffect(() => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            setSupported(false); return;
        }
        (async () => {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            setChecked(!!sub && Notification.permission === 'granted');
        })();
    }, []);

    async function toggle(on: boolean) {
        try {
            const reg = await navigator.serviceWorker.ready;
            if (on) {
                const perm = await Notification.requestPermission();
                if (perm !== 'granted') {
                    setChecked(false);
                    onNote('Push permission denied. Enable in browser settings, then retry.');
                    return;
                }
                const { public_key } = await api.get<{ public_key: string }>('/api/push/vapid-key');
                let sub = await reg.pushManager.getSubscription();
                if (!sub) {
                    sub = await reg.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(public_key) as BufferSource,
                    });
                }
                const body = { ...sub.toJSON(), user_agent: navigator.userAgent };
                await api.post('/api/push/subscribe', body);
                setChecked(true);
                onNote('✓ Push notifications on — you\'ll get them even when OnHere is closed.');
            } else {
                const sub = await reg.pushManager.getSubscription();
                if (sub) await sub.unsubscribe();
                setChecked(false);
                onNote('Push notifications off.');
            }
        } catch (err) {
            onNote('Push setup failed: ' + (err as Error).message);
            setChecked(prev => !prev);
        }
    }

    if (!supported) return (
        <label className="inline-toggle" style={{ opacity: 0.6 }}>
            <input type="checkbox" disabled />
            <span>
                <strong>Send push notifications</strong>
                <em>Your browser doesn't support push notifications.</em>
            </span>
        </label>
    );
    return (
        <label className="inline-toggle">
            <input type="checkbox" checked={checked} onChange={(e) => toggle(e.target.checked)} />
            <span>
                <strong>Send push notifications</strong>
                <em>Get replies, comments, and mentions on your phone even when OnHere isn't open.</em>
            </span>
        </label>
    );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
}

// ---- Avatar section ----
function AvatarSection({ me, onChanged }: { me: Me; onChanged: (u: Me) => void }) {
    const api = useApi();
    const [uploaded, setUploaded] = useState<UploadResult | null>(null);
    const [status,   setStatus]   = useState<string>('');

    async function save(u: UploadResult | null) {
        setUploaded(u);
        if (!u) {
            // Cleared — call DELETE.
            try {
                await api.del('/api/me/avatar');
                onChanged({ ...me, avatar_url: null });
                setStatus('Avatar removed.');
            } catch (e) { setStatus('Remove failed: ' + (e as Error).message); }
            return;
        }
        const url = u.thumbnail_url || u.url;
        try {
            const next = await api.put<Me>('/api/me/avatar', { avatar_url: url });
            onChanged(next);
            setStatus('✓ Avatar updated.');
        } catch (e) { setStatus('Save failed: ' + (e as Error).message); }
    }

    return (
        <section className="profile-block">
            <h2>Avatar</h2>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 12 }}>
                <Avatar src={me.avatar_url} name={me.display_name} seed={me.id} size={96} />
                <div style={{ fontFamily: "'Newsreader',serif", color: '#555' }}>
                    Your avatar shows next to everything you post.
                    Square works best; big images are resized to 512px.
                </div>
            </div>
            <MediaUpload value={uploaded} onChange={save} />
            {status && <p style={{ marginTop: 8, color: 'var(--accent)', fontSize: 12 }}>{status}</p>}
        </section>
    );
}

// --------------------------------------------------------------- Follows

interface FollowUser {
    id: string; display_name: string; avatar_url?: string | null; created_at: string;
}
function FollowsSection() {
    const api = useApi();
    const [pending, setPending] = useState<FollowUser[]>([]);
    const [auto,    setAuto]    = useState<boolean | null>(null);
    const [status,  setStatus]  = useState('');

    const load = useCallback(async () => {
        try {
            const p = await api.get<{ users: FollowUser[] }>('/api/me/follow-requests');
            setPending(p.users ?? []);
            // Auto-accept lives on the user row; we fetch /api/me to read it. Cheap.
            const me = await api.get<{ follow_auto_accept?: boolean }>('/api/me');
            setAuto(!!me.follow_auto_accept);
        } catch (e) { console.error('follows load', e); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    async function toggleAuto(on: boolean) {
        try {
            await api.put('/api/me/follow-auto-accept', { on });
            setAuto(on);
            setStatus(on ? '✓ New follow requests will accept themselves.' : '✓ New follow requests will queue for your approval.');
            setTimeout(() => setStatus(''), 3000);
        } catch (e) { setStatus('Save failed: ' + (e as Error).message); }
    }
    async function decide(followerID: string, accept: boolean) {
        try {
            await api.post('/api/me/follow-requests/' + followerID + (accept ? '/accept' : '/reject'));
            setPending(pending.filter(u => u.id !== followerID));
        } catch (e) { alert((e as Error).message); }
    }

    return (
        <section className="profile-block">
            <h2>Following &amp; followers</h2>
            <label className="inline-toggle">
                <input type="checkbox" checked={!!auto} onChange={e => toggleAuto(e.target.checked)} />
                <span>
                    <strong>Auto-accept follow requests</strong>
                    <em>
                        ON = neighbours who tap Follow on your profile see your posts
                        immediately. OFF (default) = they land in your inbox and you
                        pick who gets in.
                    </em>
                </span>
            </label>
            {status && <p style={{ color: 'var(--accent)', fontSize: 12, marginTop: 4 }}>{status}</p>}

            <h3 style={{ marginTop: 16, fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, fontFamily: '-apple-system, sans-serif' }}>
                Pending requests ({pending.length})
            </h3>
            {pending.length === 0 ? (
                <p style={{ color: '#999', fontStyle: 'italic', fontFamily: "'Newsreader',serif" }}>Nothing waiting.</p>
            ) : pending.map(u => (
                <div key={u.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 8, borderBottom: '1px dotted #cfc9b7' }}>
                    <Avatar src={u.avatar_url} name={u.display_name} seed={u.id} size={36} />
                    <div style={{ flex: 1 }}>
                        <Link to={'/user/' + u.id} style={{ fontWeight: 700 }}>{u.display_name}</Link>
                        <div style={{ fontSize: 11, color: '#666' }}>Requested {new Date(u.created_at).toLocaleDateString()}</div>
                    </div>
                    <button type="button" onClick={() => decide(u.id, true)}
                            style={{ padding: '4px 10px', background: 'var(--accent)', color: '#fff', border: 0, cursor: 'pointer', fontSize: 12 }}>
                        Accept
                    </button>
                    <button type="button" onClick={() => decide(u.id, false)}
                            style={{ padding: '4px 10px', background: 'none', color: '#8b1919', border: '1px solid #8b1919', cursor: 'pointer', fontSize: 12 }}>
                        Reject
                    </button>
                </div>
            ))}
        </section>
    );
}

// --------------------------------------------------------------- Location

// LocationSection — three opt-in toggles + a "delete my location" button.
// Every switch is off-by-default except emergency alerts (which are
// genuinely useful and default ON per the privacy spec).
interface LocationPrefs {
    share_home_area:                boolean;
    local_business_pushes_opt_in:   boolean;
    emergency_alerts_opt_in:        boolean;
    home_lat_cell?:                 number | null;
    home_lon_cell?:                 number | null;
}
function LocationSection() {
    const api = useApi();
    const [prefs, setPrefs] = useState<LocationPrefs | null>(null);
    const [status, setStatus] = useState('');

    const load = useCallback(async () => {
        try {
            const p = await api.get<LocationPrefs>('/api/me/location-prefs');
            setPrefs(p);
        } catch (e) { console.error('load location prefs', e); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    async function patch(next: Partial<LocationPrefs> & { set_home_coord?: boolean; home_lat?: number | null; home_lon?: number | null }) {
        try {
            const p = await api.patch<LocationPrefs>('/api/me/location-prefs', next);
            setPrefs(p);
            setStatus('✓ Saved.');
            setTimeout(() => setStatus(''), 2000);
        } catch (e) { setStatus('Save failed: ' + (e as Error).message); }
    }

    async function useBrowserLocation() {
        if (!navigator.geolocation) { setStatus('Browser has no location API.'); return; }
        setStatus('Asking browser…');
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                await patch({ set_home_coord: true, home_lat: pos.coords.latitude, home_lon: pos.coords.longitude });
            },
            (err) => setStatus('Location denied: ' + err.message),
            { enableHighAccuracy: false, maximumAge: 3600_000, timeout: 15_000 },
        );
    }

    if (!prefs) return null;
    return (
        <section className="profile-block">
            <h2>Location &amp; alerts</h2>
            <p style={{ color: '#555', fontFamily: "'Newsreader',serif", marginTop: 0 }}>
                We collect what we need to build a useful hyperlocal paper — nothing more.
                Every switch below is yours to flip. Your exact address never leaves
                your browser; we only store a coarse cell (~100 m grid).
            </p>

            <label className="inline-toggle">
                <input type="checkbox"
                       checked={prefs.share_home_area}
                       onChange={(e) => patch({ share_home_area: e.target.checked })} />
                <span>
                    <strong>Share my area for local relevance</strong>
                    <em>
                        Lets us show you stories, events and places from your part of the
                        suburb — not just the community as a whole. Turn OFF to erase your
                        stored cell entirely.
                    </em>
                </span>
            </label>

            {prefs.share_home_area && (
                <div style={{ marginLeft: 26, marginBottom: 12, fontSize: 12, color: '#555' }}>
                    {prefs.home_lat_cell != null
                        ? <>Home cell set: {prefs.home_lat_cell.toFixed(3)}, {prefs.home_lon_cell?.toFixed(3)}. </>
                        : <>No cell set. </>}
                    <button type="button" onClick={useBrowserLocation}
                            style={{ background: 'none', border: 0, color: 'var(--accent)', cursor: 'pointer', padding: 0, textDecoration: 'underline', font: 'inherit' }}>
                        Use my current location
                    </button>
                </div>
            )}

            <label className="inline-toggle">
                <input type="checkbox"
                       checked={prefs.local_business_pushes_opt_in}
                       onChange={(e) => patch({ local_business_pushes_opt_in: e.target.checked })} />
                <span>
                    <strong>Push me offers from local businesses</strong>
                    <em>
                        "Fresh bagels in 20 min" — you'll only get pings from businesses
                        within a radius they set. Rate-limited. Silence any business
                        with one tap.
                    </em>
                </span>
            </label>

            <label className="inline-toggle">
                <input type="checkbox"
                       checked={prefs.emergency_alerts_opt_in}
                       onChange={(e) => patch({ emergency_alerts_opt_in: e.target.checked })} />
                <span>
                    <strong>Emergency &amp; civic alerts</strong>
                    <em>
                        Fires, load-shedding schedule changes, boil-water orders from
                        your community. Default ON — turn off if you'd rather find out
                        from the paper.
                    </em>
                </span>
            </label>

            <ReviewVisitsToggle />
            <VisitHistoryPanel />
            {status && <p style={{ marginTop: 8, color: 'var(--accent)', fontSize: 12 }}>{status}</p>}
        </section>
    );
}

// ------------------------------------------------------------- Visit prefs

// ReviewVisitsToggle — passive presence detection → review nudge.
// Tab periodically POSTs coarse coords to /api/me/presence; server
// matches against place-y groups within 50m; hourly cron sends a bell
// nudge 24h later. Default OFF.
function ReviewVisitsToggle() {
    const api = useApi();
    const [on, setOn] = useState<boolean | null>(null);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        api.get<{ review_visits_opt_in?: boolean }>('/api/me')
            .then(m => setOn(!!m.review_visits_opt_in))
            .catch(() => setOn(false));
    }, [api]);

    async function flip(next: boolean) {
        try {
            await api.put('/api/me/review-visits-opt-in', { on: next });
            setOn(next);
            setMsg(next
                ? '✓ On — flipping OFF later wipes your visit history.'
                : '✓ Off — your visit history has been deleted.');
            setTimeout(() => setMsg(''), 4000);
        } catch (e) { setMsg('Save failed: ' + (e as Error).message); }
    }
    if (on === null) return null;
    return (
        <>
            <label className="inline-toggle" style={{ marginTop: 8 }}>
                <input type="checkbox" checked={on} onChange={e => flip(e.target.checked)} />
                <span>
                    <strong>Nudge me to review places I visit</strong>
                    <em>
                        Coarse-only presence check every ~5 min while OnHere is open.
                        We match against local businesses and, if you spent time near
                        one, send you a bell notification a day later to leave a
                        review. Turning OFF deletes every stored visit.
                    </em>
                </span>
            </label>
            {msg && <p style={{ marginTop: 4, color: 'var(--accent)', fontSize: 12 }}>{msg}</p>}
        </>
    );
}

interface VisitRow {
    id: string; group_id: string; group_slug: string; group_name: string;
    day: string; last_seen_at: string; minutes: number; notified_at?: string | null;
}
function VisitHistoryPanel() {
    const api = useApi();
    const [items, setItems] = useState<VisitRow[] | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get<{ visits: VisitRow[] }>('/api/me/visits');
            setItems(r.visits ?? []);
        } catch { setItems([]); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    async function wipe() {
        if (!window.confirm('Delete every visit row? Can\'t be undone.')) return;
        try {
            await api.del('/api/me/visits');
            await load();
        } catch (e) { alert((e as Error).message); }
    }
    if (items === null) return null;
    if (items.length === 0) return null;   // no history, no panel
    return (
        <div style={{ marginTop: 12, padding: 10, background: '#faf8f2', border: '1px dashed #cfc9b7' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                <strong style={{ fontFamily: '-apple-system,sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2 }}>
                    Your visit history ({items.length})
                </strong>
                <button type="button" onClick={wipe}
                        style={{ padding: '4px 10px', background: 'none', color: '#8b1919', border: '1px solid #8b1919', cursor: 'pointer', fontSize: 11 }}>
                    Delete history
                </button>
            </div>
            {items.slice(0, 8).map(v => (
                <div key={v.id} style={{ padding: '4px 0', borderBottom: '1px dotted #cfc9b7', fontSize: 12 }}>
                    <Link to={'/groups/' + v.group_slug + '/dashboard'}>{v.group_name}</Link>
                    <span style={{ color: '#666', marginLeft: 8 }}>
                        · {new Date(v.day).toLocaleDateString()} · {v.minutes} min
                        {v.notified_at && ' · nudged'}
                    </span>
                </div>
            ))}
        </div>
    );
}
